import React, { useState } from "react"
import {Code2, Play, BookOpen, Zap} from 'lucide-react'
import Navbar from "./components/Navbar"
import Hero from "./components/Hero"
import ProblemSelector from "./components/ProblemSelector"
import Visualizer from "./components/Visualizer"
import CodeGenerator from "./components/CodeGenerator"
import LearnSection from "./components/LearnSection"

function App() {
  const [selectedProblem, setSelectedProblem] = useState<string>("fibonacci")

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Navbar />
      <Hero />
      
      <main className="container mx-auto px-4 py-16">
        {/* Features Section */}
        <section className="mb-20">
          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard
              icon={<Play className="w-8 h-8" />}
              title="Interactive Visualizer"
              description="Step through DP algorithms frame-by-frame and see the table fill in real-time"
              color="cyan"
            />
            <FeatureCard
              icon={<Code2 className="w-8 h-8" />}
              title="Code Generator"
              description="Generate optimized boilerplate code in Python, Java, C++, and JavaScript"
              color="emerald"
            />
            <FeatureCard
              icon={<Zap className="w-8 h-8" />}
              title="5 Core Problems"
              description="Master Fibonacci, Knapsack, LCS, Climbing Stairs, and Coin Change"
              color="amber"
            />
          </div>
        </section>

        {/* Problem Selector */}
        <ProblemSelector
          selectedProblem={selectedProblem}
          onSelectProblem={setSelectedProblem}
        />

        {/* Visualizer */}
        <Visualizer problemType={selectedProblem} />

        {/* Code Generator */}
        <CodeGenerator problemType={selectedProblem} />
      </main>

      {/* Learn Section - Educational Content for SEO */}
      <LearnSection />

      <main className="container mx-auto px-4">
        {/* Affiliate CTA */}
        <section className="mt-20 bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-8 border border-slate-700">
          <div className="flex items-start gap-4">
            <BookOpen className="w-12 h-12 text-cyan-400 flex-shrink-0" />
            <div>
              <h3 className="text-2xl font-bold mb-2">Struggling to understand Dynamic Programming?</h3>
              <p className="text-slate-300 mb-4">Master it in 2 weeks with these expert-recommended courses</p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://www.educative.io/courses/grokking-dynamic-programming-patterns-for-coding-interviews"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-2 bg-cyan-500 hover:bg-cyan-600 rounded-lg font-medium transition-colors"
                >
                  Educative.io
                </a>
                <a
                  href="https://zerotomastery.io/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg font-medium transition-colors"
                >
                  Zero To Mastery
                </a>
                <a
                  href="https://www.algoexpert.io/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-2 bg-amber-500 hover:bg-amber-600 rounded-lg font-medium transition-colors"
                >
                  AlgoExpert
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-800 mt-20 py-8">
        <div className="container mx-auto px-4 text-center text-slate-400">
          <p>© 2024 DP Generator. Master Dynamic Programming with Interactive Visualizations.</p>
        </div>
      </footer>
    </div>
  )
}

interface FeatureCardProps {
  icon: React.ReactNode
  title: string
  description: string
  color: "cyan" | "emerald" | "amber"
}

function FeatureCard({ icon, title, description, color }: FeatureCardProps) {
  const colorClasses = {
    cyan: "from-cyan-500/20 to-cyan-500/0 border-cyan-500/30 text-cyan-400",
    emerald: "from-emerald-500/20 to-emerald-500/0 border-emerald-500/30 text-emerald-400",
    amber: "from-amber-500/20 to-amber-500/0 border-amber-500/30 text-amber-400"
  }

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} border rounded-xl p-6 hover:scale-105 transition-transform`}>
      <div className="mb-4">{icon}</div>
      <h3 className="text-xl font-bold mb-2 text-white">{title}</h3>
      <p className="text-slate-300">{description}</p>
    </div>
  )
}

export default App
